import { useQueryClient } from '@tanstack/react-query';
import { Box } from 'grommet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Router } from 'preact-router';
import { connect, useDispatch } from 'react-redux';

import AppLoader from '@/components/AppLoader';
import ContactUsModalLoader from '@/components/async/ContactUsModalLoader';
import InstallAppModalLoader from '@/components/async/InstallAppModalLoader';
import AuthGuard from '@/components/Authorize/AuthGuard';
import GuestOnlyGuard from '@/components/Authorize/GuestOnlyGuard';
import HabitatsUpdater from '@/components/HabitatsUpdater';
import Redirect from '@/components/Redirect';
import SpeedyFlow from '@/components/SpeedyFlow';
import speedyFlowSlice from '@/components/SpeedyFlow/redux/slice';
import TermsAndConditions from '@/components/TermsAndConditions';
import { PRIVACY_PDF_URL, TERMS_PDF_URL } from '@/components/TermsAndConditions/constants';
import WhatsNew from '@/components/WhatsNew';
import { loadPage, logAndGetCampaignData } from '@/helpers';
import { useSubscribeNotificationsHandler } from '@/hooks/notifications';
import { setQueryParams } from '@/hooks/route';
import { invalidateUser } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { setIsRealEmail, setReferralData } from '@/redux/slices/user/actions';
// Code-splitting is automated for `routes` directory
import Account from '@/routes/account';
import Album from '@/routes/album';
import CheckoutCancelled from '@/routes/checkout/CheckoutCancelled';
import CheckoutCompleted from '@/routes/checkout/CheckoutCompleted';
import EmailVerify from '@/routes/emailVerify';
import EmbeddedHabitat from '@/routes/embed/EmbeddedHabitat';
import Feed from '@/routes/feed';
import Gift from '@/routes/gift';
import Habitat from '@/routes/habitat';
import Habitats from '@/routes/habitats';
import Help from '@/routes/help';
import Highlights from '@/routes/highlights';
import HomeWithLayout from '@/routes/home';
import Login from '@/routes/login';
import HabitatsView from '@/routes/MapView';
import MeRedirect from '@/routes/me';
import MyAlbumLayoutDetector from '@/routes/myAlbum/MyAlbumLayoutDetector';
import NotFound from '@/routes/notFound';
import Notifications from '@/routes/notifications';
import PartnerDonationRedirect from '@/routes/partnerDonationRedirect';
import PasswordReset from '@/routes/passwordReset';
import Redeem from '@/routes/redeem';
import Schedule from '@/routes/schedule';
import ScheduleMobileLayout from '@/routes/schedule/MobileLayout';
import Signup from '@/routes/signup';
import SocialLogin from '@/routes/socialLogin';
import SoftReload from '@/routes/softReload';
import Start from '@/routes/start';
import TalkSchedule from '@/routes/talkSchedule';
import TermsAndPrivacy from '@/routes/TermsAndPrivacy';
import Welcome from '@/routes/welcome';
import Zoo from '@/routes/zoo';
import { EDUCATORS_PATH_PREFIX, toEducatorsURL } from '@/shared/educatorsApp';
import { buildURL, patch, post } from '@/shared/fetch';
import { logPageViewGA } from '@/shared/ga';
import { trackMP } from '@/shared/mp';
import {
  goToPostAuthDestination,
  savePostAuthDestination,
  shouldSaveRedirectForPath,
  syncPostAuthDestinationForNavigation,
} from '@/shared/postAuthDestination';
import { useSlideDownPromptPushCategories, useUpdatePushNotificationData } from '@/shared/pushNotifications/hooks';
import { recordUtmHabitatFromSearchParams } from '@/shared/visitedHabitats';

import GuestExperienceController from '../GuestExperienceController';
import IframeView from '../IframeView/IframeView';
import { LayoutDetector } from '../LayoutDetector';
import DefaultLandingRedirect from '../LayoutDetector/DefaultLandingRedirect';
import { useDefaultLandingPath } from '../LayoutDetector/hooks';
import AdminRouter from './AdminRouter';
import AutoReadNotificationsHandler from './AutoReadNotificationsHandler';

const EducatorRedirect = ({ matches }) => {
  const educatorsAppURL = useSelector((state) => state.remoteConfig.educatorsAppURL);
  useEffect(() => {
    const target = toEducatorsURL(
      educatorsAppURL,
      `${EDUCATORS_PATH_PREFIX}/${matches.educatorPath || ''}`,
      window.location.search,
    );
    if (target) {
      loadPage(`${target}${window.location.hash}`);
    }
  }, [matches, educatorsAppURL]);
  return null;
};

const Main = ({ onRouteChange, showContactUs, logged, sessionChecked, timezone, showInstallApp }) => {
  const dispatch = useDispatch();
  // Only tracks whether the router has resolved the first route, to choose AppLoader vs NotFound
  // at the fallback path. The actual route is driven by preact-router from the URL.
  const [isAppInitialized, setIsAppInitialized] = useState(false);

  const isGiftCardUser = useMemo(() => {
    if (typeof document !== 'undefined') {
      const { searchParams } = new URL(window.location);
      return searchParams.get('isGiftCardUser') === 'true';
    }
    return false;
  }, []);

  const defaultLandingPath = useDefaultLandingPath();

  useEffect(() => {
    const updateRefData = async () => {
      const campaignData = await logAndGetCampaignData();
      dispatch(setReferralData(campaignData));
    };
    updateRefData();
  }, [dispatch]);

  useEffect(() => {
    const { timeZone: clientTimezone } = Intl.DateTimeFormat().resolvedOptions();
    if (logged && timezone !== clientTimezone) {
      patch(buildURL('/users/timezone'), { timezone: clientTimezone }).catch((error) =>
        console.error('Failed to update timezone', error),
      );
    }
  }, [logged, timezone]);

  const queryClient = useQueryClient();

  const emailVerificationHandler = useCallback(
    async (token, email, source) => {
      try {
        const { valid } = await post(buildURL('/users/emailVerification'), { token, email, source });
        if (valid) {
          dispatch(setIsRealEmail());
          // And tell the query, or the fetch already in flight at page load answers with the state
          // from before this and puts `isRealEmail: false` straight back — leaving the visitor on a
          // page still asking for the code it has just accepted.
          invalidateUser(queryClient);
        }
      } catch (err) {
        console.error('email verification error: ', err);
      } finally {
        // Take it off the URL, spent or not. It carries a one-shot code *and* the visitor's email
        // address, and it is otherwise left sitting in their history, in any bookmark they keep,
        // and in `document.referrer` for whatever the page loads next. Both values are already in
        // hand by then, so removing them costs nothing.
        //
        // After the await, not before: this runs from the router's `onChange`, which preact-router
        // calls *during* render, and routing from inside a render is dropped on the floor.
        //
        // Replacing re-enters this handler with a clean URL, which is also how the page view
        // finally gets logged — the branch that sends us here returns before that, so a
        // verification landing recorded nothing at all, and would otherwise have recorded an
        // address.
        setQueryParams({ emailVerificationCode: undefined, verificationEmail: undefined }, true);
      }
    },
    [dispatch, queryClient],
  );

  // The page the anonymous visitor first arrived on, seeded at mount (before any guard redirect)
  // and updated on each eligible navigation. Used as the destination to restore after auth.
  const pendingNavigationRef = useRef(
    typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined,
  );

  // Single source of truth for post-auth destination handling. Idempotent, so it can be invoked
  // from both route change (onChange) and session-check change (effect) without duplication:
  // - anonymous: persist the intended destination
  // - logged-in arriving with ?redirect=: consume it and redirect immediately
  const syncPostAuthDestination = useCallback(
    (navigationUrl) => {
      if (typeof window === 'undefined') {
        return;
      }

      const candidate = navigationUrl ?? `${window.location.pathname}${window.location.search}`;

      // Before the session resolves we can't yet tell anonymous from logged-in, and a guard may
      // redirect the moment it resolves (e.g. /redeem -> /login, a private zoo page -> /home).
      // Remember the intended (eligible) landing now so that redirect can't erase it. Reading
      // window.location later — at session-check time — would already be the post-redirect URL.
      if (!sessionChecked) {
        if (shouldSaveRedirectForPath(candidate.split('?')[0])) {
          pendingNavigationRef.current = candidate;
        }
        return;
      }

      if (logged) {
        // Discard any remembered landing: a logged-in user has no post-auth destination to
        // restore, and keeping it could resurrect a stale destination after a later logout.
        pendingNavigationRef.current = undefined;
        if (window.location.href.includes('socialLogin')) {
          return;
        }
        // An explicit ?redirect= on an authenticated page is a deep-link handoff (e.g. from the
        // native app). It intentionally bypasses the onboarding/email gate and redirects
        // immediately — this preserves the long-standing behavior from before the post-auth
        // destination refactor (previously a bare `route(redirect, true)`). The only change is
        // that it now runs through savePostAuthDestination/goToPostAuthDestination, which adds
        // trusted-redirect validation (and native-scheme/external-URL support).
        const redirectToParam = new URLSearchParams(window.location.search).get('redirect');
        if (redirectToParam) {
          savePostAuthDestination(redirectToParam);
          goToPostAuthDestination();
        }
        return;
      }

      // Anonymous with the session resolved: persist the pre-session landing if we captured one
      // (covers guard-redirect races), otherwise the current navigation. Consume it afterwards so
      // ordinary client-side navigations use their own URL.
      const target = pendingNavigationRef.current ?? candidate;
      pendingNavigationRef.current = undefined;
      syncPostAuthDestinationForNavigation(target, { logged, sessionChecked });
    },
    [logged, sessionChecked],
  );

  // Run on session-check change (and mount). Route changes are covered by onChange below.
  useEffect(() => {
    syncPostAuthDestination();
  }, [syncPostAuthDestination]);

  const routerChangeHandler = useCallback(
    (props) => {
      const {
        url,
        current: {
          props: { matches },
        },
      } = props;

      if (url.startsWith('/checkout-completed')) {
        setIsAppInitialized(true);
        return;
      }

      if (matches.emailVerificationCode && matches.verificationEmail) {
        emailVerificationHandler(matches.emailVerificationCode, matches.verificationEmail, 'link');
        return;
      }

      syncPostAuthDestination(url);
      recordUtmHabitatFromSearchParams(window.location.search);
      // The Speedy Flow lives in redux, not in the route, so it has to be told. `route()` uses
      // pushState and emits no event, which is why listening for `popstate` alone left the modal
      // floating over pages it had nothing to do with — an expired session bouncing to /login being
      // the worst case. Path only: query strings change under a running flow without it moving.
      dispatch(speedyFlowSlice.actions.speedyFlowNavigated(url.split('?')[0]));

      setIsAppInitialized(true);
      logPageViewGA(url);
      trackMP('page-view');

      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'PageView');
      }

      onRouteChange(props);
    },
    [dispatch, emailVerificationHandler, onRouteChange, syncPostAuthDestination],
  );

  // Resolve the session at the app level so post-auth destination capture works on every route —
  // including unguarded ones (e.g. the NotFound catch-all). Otherwise the sync below silently
  // no-ops on routes that never trigger a user fetch. The hook is idempotent (fetches once).

  useUpdatePushNotificationData();
  useSlideDownPromptPushCategories();

  useSubscribeNotificationsHandler();

  const routes = (
    <Box fill className="calculated-full-height">
      <Router onChange={routerChangeHandler}>
        {/* redirects to /highlights on mobile, /home on desktop */}
        <DefaultLandingRedirect path="/" />
        <DefaultLandingRedirect path="/map" />

        {/* This is an intermediate route to soft reload the page when clicking on a notification */}
        {/* You should use `to` query param to route to the destination */}
        <SoftReload path="/redirect" title="Loading..." />

        {/* Redirect to the educators app (currently only used for checkout process) */}
        <EducatorRedirect path="/educators/:educatorPath*" />

        {/* Render the same component for authenticated and unauthenticated users */}
        <AuthGuard path="/home" publicPath={false} title="Home" loggedInOnly fallback={<HomeWithLayout />}>
          <HomeWithLayout />
        </AuthGuard>

        <Redirect path="/prices" to="/home" />

        <SocialLogin path="/socialLogin" />
        <CheckoutCompleted path="/checkout-completed" />
        <CheckoutCancelled path="/checkout-cancelled" />

        <Login path="/login/token/:token" title="Login" />

        <GuestOnlyGuard path="/login" redirectRoute={defaultLandingPath} title="Login">
          <Login />
        </GuestOnlyGuard>

        <GuestOnlyGuard path="/signup" title="Sign Up" redirectRoute={isGiftCardUser ? '/redeem' : defaultLandingPath}>
          <Signup />
        </GuestOnlyGuard>

        <GuestOnlyGuard path="/help" title="Help" fallback={<Help />}>
          <Help />
        </GuestOnlyGuard>

        <PasswordReset path="/passwordReset" title="Reset Password" />

        {/* display 404 instead of Unatuhorized message
            we don't want our viewers to know about this route
            and still redirect unauthenticated users to /login page
            this redirection is there not to confuse our admins */}
        <AuthGuard path="/admin/:*" title="Admin" adminOnly fallback={<NotFound />}>
          <AdminRouter />
        </AuthGuard>

        {/* Render the same component for authenticated and unauthenticated users */}
        <AuthGuard path="/highlights" publicPath={false} title="Highlights" loggedInOnly fallback={<Highlights />}>
          <Highlights />
        </AuthGuard>

        <AuthGuard path="/feed" title="Community Feed" loggedInOnly redirectTo="/login">
          <Feed />
        </AuthGuard>

        <AuthGuard path="/habitats" title="My Animals" loggedInOnly redirectTo="/home">
          <Habitats />
        </AuthGuard>

        <AuthGuard path="/welcome" redirectTo="/home" loggedInOnly>
          <Welcome />
        </AuthGuard>

        <Zoo path="/h/:zooSlug" skipTitle />
        <Habitat path="/h/:zooName/:habitatSlug" skipTitle />

        <AuthGuard path="/notifications" title="Notifications" redirectTo="/home" loggedInOnly>
          <Notifications />
        </AuthGuard>
        <AuthGuard path="/schedule" title="Schedule" publicPath="/talk-schedule" loggedInOnly redirectTo="/talk-schedule">
          <LayoutDetector desktop={<TalkSchedule />} mobile={<ScheduleMobileLayout />} title="Events" />
        </AuthGuard>
        <GuestOnlyGuard path="/talk-schedule" title="Schedule" redirectRoute="/schedule" overrideHistory>
          <LayoutDetector desktop={<TalkSchedule />} mobile={<ScheduleMobileLayout title="Events" />} />
        </GuestOnlyGuard>
        <AuthGuard path="/me" loggedInOnly publicPath="/login">
          <MeRedirect />
        </AuthGuard>
        <GuestOnlyGuard title="My Journal" path="/u/:userName" fallback={<MyAlbumLayoutDetector />}>
          <MyAlbumLayoutDetector />
        </GuestOnlyGuard>
        <AuthGuard path="/redeem" loggedInOnly redirectTo="/login">
          <Redeem />
        </AuthGuard>
        <AuthGuard path="/emailVerification" loggedInOnly redirectTo="/login">
          <EmailVerify />
        </AuthGuard>
        <Gift path="/gift" />
        <PartnerDonationRedirect path="/partnerDonation" />
        <Redirect path="/favorite" to="/habitats" />
        <AuthGuard path="/account/:activeTab" loggedInOnly redirectTo="/login">
          <Account />
        </AuthGuard>
        <Redirect path="/plans" to="/start?ppt=premium" />
        <AuthGuard path="/start" loggedInOnly redirectTo="/login">
          <Start />
        </AuthGuard>

        {/* deprecated routes, we moved them to webflow */}
        <TermsAndPrivacy path="/terms-and-conditions" title="Terms and Conditions" pdfLink={TERMS_PDF_URL} />
        <TermsAndPrivacy path="/privacy-policy" title="Privacy Policy" pdfLink={PRIVACY_PDF_URL} />

        <Album mediaType="photos" path="/album/photos/:photoId" />
        <Album mediaType="videos" path="/album/videos/:videoId" />

        {/* TODO remove /mapView once its updated on the torontozoo website */}
        {/* Keep for backwards compatibility for partners using this url */}
        <IframeView linkTarget="_blank" path="/mapView" key="iframe-habitats">
          <HabitatsView withPadding />
        </IframeView>
        <IframeView linkTarget="_blank" path="/embed/map-view" key="iframe-map-view">
          <HabitatsView withPadding />
        </IframeView>
        <IframeView path="/embed/schedules-view" key="embed-schedule">
          <Schedule />
        </IframeView>
        <IframeView path="/embed/habitats/:habitatSlug" key="embed-habitat">
          <EmbeddedHabitat />
        </IframeView>
        <IframeView path="/embed/habitats" key="embed-habitats">
          <HabitatsView />
        </IframeView>
        <Redirect path="/embed/price-cards" to="/home" />

        {/* NOTE: NotFound need to be at the end, on first render path is undefined */}
        {!isAppInitialized ? <AppLoader path=":*" /> : <NotFound path=":*" />}
      </Router>

      <TermsAndConditions />
      <ContactUsModalLoader isOpen={showContactUs} />
      <InstallAppModalLoader isOpen={showInstallApp} />
      <HabitatsUpdater />
      <WhatsNew />
      <SpeedyFlow />

      <AutoReadNotificationsHandler />
      <GuestExperienceController />
    </Box>
  );

  return routes;
};

export default connect(
  ({
    user: { logged, sessionChecked, timezone, lastLogin },
    modals: {
      contactus: { isOpen: showContactUs },
      installApp: { isOpen: showInstallApp },
    },
  }) => ({
    showContactUs,
    showInstallApp,
    logged,
    sessionChecked,
    timezone,
    lastLogin,
  }),
)(Main);
